// @core/stores/workflow/WorkflowStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { workflowApi } from '@core/api';
import type {
  AgentToolResponse,
  CancelExecutionRequest,
  CancelPipelineRunAsRequestRequest,
  CaptureTestCaseRequest,
  CreateAgentToolRequest,
  CreateCredentialRequest,
  CreateEnvironmentRequest,
  CreatePipelineRequest,
  CreatePromptTemplateRequest,
  CreateScheduleRequest,
  CreateTemplateRequest,
  CreateTestCaseRequest,
  CreateTestSuiteRequest,
  CredentialResponse,
  DebugSessionResponse,
  DebugStepRequest,
  DecidePipelineRunAsRequestRequest,
  DelegateDecisionRequest,
  DiffPipelineVersionsResponse,
  DuplicatePipelineRequest,
  EventDescriptorResponse,
  ExecutePipelineRequest,
  ExportPipelineYAMLResponse,
  GenerateTestCaseRequest,
  GetExecutionLogsResponse,
  GetPipelineRunAsEligibilityRequest,
  GetPipelineRunAsEligibilityResponse,
  GetProductManifestResponse,
  ImportPipelineYAMLRequest,
  InstallProductPipelinesRequest,
  InstallProductPipelinesResponse,
  InstallTemplateRequest,
  ListAgentToolsResponse,
  ListCredentialsResponse,
  ListEnvironmentsResponse,
  ListEventDescriptorsResponse,
  ListExecutionsResponse,
  ListPendingDecisionsResponse,
  ListPipelineRunAsRequestsRequest,
  ListPipelineRunAsRequestsResponse,
  ListPipelineVersionsResponse,
  ListPipelinesResponse,
  ListPromptTemplatesResponse,
  ListRegisteredProductsResponse,
  ListSchedulesResponse,
  ListTemplatesResponse,
  ListTestCasesResponse,
  ListTestResultsResponse,
  NodeType,
  NodeTypeResponse,
  PauseExecutionRequest,
  PausePipelineRequest,
  PauseScheduleRequest,
  PendingDecisionResponse,
  PipelineEnvironmentResponse,
  PipelineExecutionResponse,
  PipelineResponse,
  PipelineRunAsRequestResponse,
  PipelineScheduleResponse,
  PipelineTemplateResponse,
  PipelineVersionResponse,
  PromptTemplateResponse,
  PublishPipelineRequest,
  RegisterNodeTypeRequest,
  ResumeExecutionRequest,
  ResumePipelineRequest,
  ResumeScheduleRequest,
  RetryExecutionRequest,
  RevertToVersionRequest,
  RevokePipelineRunAsAuthorizationRequest,
  RunTestCaseRequest,
  RunTestSuiteRequest,
  SaveDraftRequest,
  SetBreakpointRequest,
  SetPipelineRunAsRequest,
  SetPipelineRunAsResponse,
  StartDebugSessionRequest,
  StopDebugSessionRequest,
  SubmitDecisionRequest,
  SyncProductManifestRequest,
  SyncProductManifestResponse,
  SyncProductPipelinesRequest,
  SyncProductPipelinesResponse,
  TestCaseResponse,
  TestCredentialRequest,
  TestCredentialResponse,
  TestPromptTemplateRequest,
  TestPromptTemplateResponse,
  TestResultResponse,
  TestSuiteResponse,
  TestSuiteRunResponse,
  ListNodeTypesRequest,
  TriggerWebhookRequest,
  TriggerWebhookResponse,
  UpdateAgentToolRequest,
  UpdateCredentialRequest,
  UpdateEnvironmentRequest,
  UpdateNodeTypeRequest,
  UpdatePipelineRequest,
  UpdatePromptTemplateRequest,
  UpdateScheduleRequest,
  UpdateTestCaseRequest,
  ValidatePipelineYAMLRequest,
  ValidatePipelineYAMLResponse,
} from '@core/types/workflow';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class WorkflowStore {
  // List state
  items: NodeType[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: NodeType | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, NodeType>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): NodeType | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: NodeType | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Cancel execution
   */
  async CancelExecution(execution_id: string, data: CancelExecutionRequest): Promise<PipelineExecutionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.CancelExecution(execution_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Cancel a pending execution identity request
   */
  async CancelPipelineRunAsRequest(request_id: string, data: CancelPipelineRunAsRequestRequest): Promise<PipelineRunAsRequestResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.CancelPipelineRunAsRequest(request_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Capture execution as test case
   */
  async CaptureTestCase(execution_id: string, data: CaptureTestCaseRequest): Promise<TestCaseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.CaptureTestCase(execution_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create agent tool
   */
  async CreateAgentTool(data: CreateAgentToolRequest): Promise<AgentToolResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.CreateAgentTool(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create credential
   */
  async CreateCredential(data: CreateCredentialRequest): Promise<CredentialResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.CreateCredential(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create environment
   */
  async CreateEnvironment(pipeline_id: string, data: CreateEnvironmentRequest): Promise<PipelineEnvironmentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.CreateEnvironment(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create a new pipeline
   */
  async CreatePipeline(data: CreatePipelineRequest): Promise<PipelineResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.CreatePipeline(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create prompt template
   */
  async CreatePromptTemplate(data: CreatePromptTemplateRequest): Promise<PromptTemplateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.CreatePromptTemplate(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create pipeline schedule
   */
  async CreateSchedule(pipeline_id: string, data: CreateScheduleRequest): Promise<PipelineScheduleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.CreateSchedule(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create pipeline template
   */
  async CreateTemplate(data: CreateTemplateRequest): Promise<PipelineTemplateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.CreateTemplate(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create test case
   */
  async CreateTestCase(pipeline_id: string, data: CreateTestCaseRequest): Promise<TestCaseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.CreateTestCase(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create test suite
   */
  async CreateTestSuite(pipeline_id: string, data: CreateTestSuiteRequest): Promise<TestSuiteResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.CreateTestSuite(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Debug step
   */
  async DebugStep(session_id: string, data: DebugStepRequest): Promise<DebugSessionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.DebugStep(session_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Approve or reject an execution identity request
   */
  async DecidePipelineRunAsRequest(request_id: string, data: DecidePipelineRunAsRequestRequest): Promise<PipelineRunAsRequestResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.DecidePipelineRunAsRequest(request_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delegate decision
   */
  async DelegateDecision(decision_id: string, data: DelegateDecisionRequest): Promise<PendingDecisionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.DelegateDecision(decision_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete agent tool
   */
  async DeleteAgentTool(tool_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await workflowApi.DeleteAgentTool(tool_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== tool_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === tool_id) {
          this.currentItem = null;
        }

        this.removeFromCache(tool_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete credential
   */
  async DeleteCredential(credential_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await workflowApi.DeleteCredential(credential_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== credential_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === credential_id) {
          this.currentItem = null;
        }

        this.removeFromCache(credential_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete environment
   */
  async DeleteEnvironment(environment_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await workflowApi.DeleteEnvironment(environment_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== environment_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === environment_id) {
          this.currentItem = null;
        }

        this.removeFromCache(environment_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete node type
   */
  async DeleteNodeType(node_type_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await workflowApi.DeleteNodeType(node_type_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== node_type_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === node_type_id) {
          this.currentItem = null;
        }

        this.removeFromCache(node_type_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete pipeline
   */
  async DeletePipeline(pipeline_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await workflowApi.DeletePipeline(pipeline_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== pipeline_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === pipeline_id) {
          this.currentItem = null;
        }

        this.removeFromCache(pipeline_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete prompt template
   */
  async DeletePromptTemplate(template_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await workflowApi.DeletePromptTemplate(template_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== template_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === template_id) {
          this.currentItem = null;
        }

        this.removeFromCache(template_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete schedule
   */
  async DeleteSchedule(schedule_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await workflowApi.DeleteSchedule(schedule_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== schedule_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === schedule_id) {
          this.currentItem = null;
        }

        this.removeFromCache(schedule_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete test case
   */
  async DeleteTestCase(test_case_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await workflowApi.DeleteTestCase(test_case_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== test_case_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === test_case_id) {
          this.currentItem = null;
        }

        this.removeFromCache(test_case_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Diff pipeline versions
   */
  async DiffPipelineVersions(version_id: string, compare_version_id: string): Promise<DiffPipelineVersionsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.DiffPipelineVersions(version_id, compare_version_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Duplicate pipeline
   */
  async DuplicatePipeline(pipeline_id: string, data: DuplicatePipelineRequest): Promise<PipelineResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.DuplicatePipeline(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Execute pipeline
   */
  async ExecutePipeline(pipeline_id: string, data: ExecutePipelineRequest): Promise<PipelineExecutionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ExecutePipeline(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Export pipeline as YAML
   */
  async ExportPipelineYAML(version_id: string): Promise<ExportPipelineYAMLResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ExportPipelineYAML(version_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Generate test case
   */
  async GenerateTestCase(pipeline_id: string, data: GenerateTestCaseRequest): Promise<TestCaseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.GenerateTestCase(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get agent tool
   */
  async GetAgentTool(tool_id: string): Promise<AgentToolResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.GetAgentTool(tool_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get credential
   */
  async GetCredential(credential_id: string): Promise<CredentialResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.GetCredential(credential_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get event descriptor
   */
  async GetEventDescriptor(event_name: string): Promise<EventDescriptorResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.GetEventDescriptor(event_name);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get execution
   */
  async GetExecution(execution_id: string): Promise<PipelineExecutionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.GetExecution(execution_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get execution logs
   */
  async GetExecutionLogs(execution_id: string): Promise<GetExecutionLogsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.GetExecutionLogs(execution_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get node type
   */
  async GetNodeType(node_type_id: string): Promise<NodeType> {
    // Check cache first
    const cached = this.cache.get(node_type_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await workflowApi.GetNodeType(node_type_id);
      const item = response.node_type;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get pending decision
   */
  async GetPendingDecision(decision_id: string): Promise<PendingDecisionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.GetPendingDecision(decision_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get pipeline by ID
   */
  async GetPipeline(pipeline_id: string): Promise<PipelineResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.GetPipeline(pipeline_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Check how an actor can be assigned to a pipeline
   */
  async GetPipelineRunAsEligibility(pipeline_id: string, data: GetPipelineRunAsEligibilityRequest): Promise<GetPipelineRunAsEligibilityResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.GetPipelineRunAsEligibility(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get an execution identity request
   */
  async GetPipelineRunAsRequest(request_id: string): Promise<PipelineRunAsRequestResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.GetPipelineRunAsRequest(request_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get pipeline version
   */
  async GetPipelineVersion(version_id: string): Promise<PipelineVersionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.GetPipelineVersion(version_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get product manifest
   */
  async GetProductManifest(product_slug: string): Promise<GetProductManifestResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.GetProductManifest(product_slug);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get prompt template
   */
  async GetPromptTemplate(template_id: string): Promise<PromptTemplateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.GetPromptTemplate(template_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get pipeline template
   */
  async GetTemplate(template_id: string): Promise<PipelineTemplateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.GetTemplate(template_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get test result
   */
  async GetTestResult(test_result_id: string): Promise<TestResultResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.GetTestResult(test_result_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Import pipeline from YAML
   */
  async ImportPipelineYAML(data: ImportPipelineYAMLRequest): Promise<PipelineResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ImportPipelineYAML(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Install product pipelines
   */
  async InstallProductPipelines(data: InstallProductPipelinesRequest): Promise<InstallProductPipelinesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.InstallProductPipelines(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Install pipeline template
   */
  async InstallTemplate(template_id: string, data: InstallTemplateRequest): Promise<PipelineResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.InstallTemplate(template_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List agent tools
   */
  async ListAgentTools(params?: PaginatedRequest): Promise<ListAgentToolsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListAgentTools(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List credentials
   */
  async ListCredentials(params?: PaginatedRequest): Promise<ListCredentialsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListCredentials(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List environments
   */
  async ListEnvironments(pipeline_id: string, params?: PaginatedRequest): Promise<ListEnvironmentsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListEnvironments(pipeline_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List event descriptors
   */
  async ListEventDescriptors(params?: PaginatedRequest): Promise<ListEventDescriptorsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListEventDescriptors(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List pipeline executions
   */
  async ListExecutions(pipeline_id: string, params?: PaginatedRequest): Promise<ListExecutionsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListExecutions(pipeline_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List visible executions by effective actor
   */
  async ListExecutionsByActor(params?: PaginatedRequest): Promise<ListExecutionsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListExecutionsByActor(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List node types
   */
  async ListNodeTypes(params?: ListNodeTypesRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await workflowApi.ListNodeTypes(params);

      runInAction(() => {
        this.items = response.items?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List pending decisions
   */
  async ListPendingDecisions(params?: PaginatedRequest): Promise<ListPendingDecisionsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListPendingDecisions(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List visible execution identity requests
   */
  async ListPipelineRunAsRequests(params?: ListPipelineRunAsRequestsRequest): Promise<ListPipelineRunAsRequestsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListPipelineRunAsRequests(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List pipeline versions
   */
  async ListPipelineVersions(pipeline_id: string, params?: PaginatedRequest): Promise<ListPipelineVersionsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListPipelineVersions(pipeline_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List pipelines
   */
  async ListPipelines(params?: PaginatedRequest): Promise<ListPipelinesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListPipelines(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List prompt templates
   */
  async ListPromptTemplates(params?: PaginatedRequest): Promise<ListPromptTemplatesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListPromptTemplates(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List registered products
   */
  async ListRegisteredProducts(params?: PaginatedRequest): Promise<ListRegisteredProductsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListRegisteredProducts(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List pipeline schedules
   */
  async ListSchedules(pipeline_id: string, params?: PaginatedRequest): Promise<ListSchedulesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListSchedules(pipeline_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List pipeline templates
   */
  async ListTemplates(params?: PaginatedRequest): Promise<ListTemplatesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListTemplates(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List test cases
   */
  async ListTestCases(pipeline_id: string, params?: PaginatedRequest): Promise<ListTestCasesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListTestCases(pipeline_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List test results
   */
  async ListTestResults(pipeline_id: string, params?: PaginatedRequest): Promise<ListTestResultsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ListTestResults(pipeline_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Pause execution
   */
  async PauseExecution(execution_id: string, data: PauseExecutionRequest): Promise<PipelineExecutionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.PauseExecution(execution_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Pause pipeline
   */
  async PausePipeline(pipeline_id: string, data: PausePipelineRequest): Promise<PipelineResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.PausePipeline(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Pause schedule
   */
  async PauseSchedule(schedule_id: string, data: PauseScheduleRequest): Promise<PipelineScheduleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.PauseSchedule(schedule_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Publish pipeline
   */
  async PublishPipeline(pipeline_id: string, data: PublishPipelineRequest): Promise<PipelineResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.PublishPipeline(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Register node type
   */
  async RegisterNodeType(data: RegisterNodeTypeRequest): Promise<NodeTypeResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.RegisterNodeType(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove breakpoint
   */
  async RemoveBreakpoint(session_id: string, breakpoint_id: string): Promise<DebugSessionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.RemoveBreakpoint(session_id, breakpoint_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resume execution
   */
  async ResumeExecution(execution_id: string, data: ResumeExecutionRequest): Promise<PipelineExecutionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ResumeExecution(execution_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resume pipeline
   */
  async ResumePipeline(pipeline_id: string, data: ResumePipelineRequest): Promise<PipelineResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ResumePipeline(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Resume schedule
   */
  async ResumeSchedule(schedule_id: string, data: ResumeScheduleRequest): Promise<PipelineScheduleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ResumeSchedule(schedule_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Retry execution
   */
  async RetryExecution(execution_id: string, data: RetryExecutionRequest): Promise<PipelineExecutionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.RetryExecution(execution_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Revert to version
   */
  async RevertToVersion(version_id: string, data: RevertToVersionRequest): Promise<PipelineVersionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.RevertToVersion(version_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Revoke a pipeline-scoped execution identity approval
   */
  async RevokePipelineRunAsAuthorization(request_id: string, data: RevokePipelineRunAsAuthorizationRequest): Promise<PipelineRunAsRequestResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.RevokePipelineRunAsAuthorization(request_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Run test case
   */
  async RunTestCase(test_case_id: string, data: RunTestCaseRequest): Promise<TestResultResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.RunTestCase(test_case_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Run test suite
   */
  async RunTestSuite(test_suite_id: string, data: RunTestSuiteRequest): Promise<TestSuiteRunResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.RunTestSuite(test_suite_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Save draft version
   */
  async SaveDraft(pipeline_id: string, data: SaveDraftRequest): Promise<PipelineVersionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.SaveDraft(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Set breakpoint
   */
  async SetBreakpoint(session_id: string, data: SetBreakpointRequest): Promise<DebugSessionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.SetBreakpoint(session_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Set or request a pipeline execution identity
   */
  async SetPipelineRunAs(pipeline_id: string, data: SetPipelineRunAsRequest): Promise<SetPipelineRunAsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.SetPipelineRunAs(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Start debug session
   */
  async StartDebugSession(pipeline_id: string, data: StartDebugSessionRequest): Promise<DebugSessionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.StartDebugSession(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Stop debug session
   */
  async StopDebugSession(session_id: string, data: StopDebugSessionRequest): Promise<DebugSessionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.StopDebugSession(session_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Submit decision
   */
  async SubmitDecision(decision_id: string, data: SubmitDecisionRequest): Promise<PendingDecisionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.SubmitDecision(decision_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Sync product manifest
   */
  async SyncProductManifest(data: SyncProductManifestRequest): Promise<SyncProductManifestResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.SyncProductManifest(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Sync product pipelines
   */
  async SyncProductPipelines(data: SyncProductPipelinesRequest): Promise<SyncProductPipelinesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.SyncProductPipelines(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Test credential
   */
  async TestCredential(credential_id: string, data: TestCredentialRequest): Promise<TestCredentialResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.TestCredential(credential_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Test prompt template
   */
  async TestPromptTemplate(template_id: string, data: TestPromptTemplateRequest): Promise<TestPromptTemplateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.TestPromptTemplate(template_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Trigger pipeline by webhook
   */
  async TriggerWebhook(webhook_token: string, data: TriggerWebhookRequest): Promise<TriggerWebhookResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.TriggerWebhook(webhook_token, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update agent tool
   */
  async UpdateAgentTool(tool_id: string, data: UpdateAgentToolRequest): Promise<AgentToolResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.UpdateAgentTool(tool_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update credential
   */
  async UpdateCredential(credential_id: string, data: UpdateCredentialRequest): Promise<CredentialResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.UpdateCredential(credential_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update environment
   */
  async UpdateEnvironment(environment_id: string, data: UpdateEnvironmentRequest): Promise<PipelineEnvironmentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.UpdateEnvironment(environment_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update node type
   */
  async UpdateNodeType(node_type_id: string, data: UpdateNodeTypeRequest): Promise<NodeType> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await workflowApi.UpdateNodeType(node_type_id, data);
      const item = response.node_type;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Update pipeline
   */
  async UpdatePipeline(pipeline_id: string, data: UpdatePipelineRequest): Promise<PipelineResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.UpdatePipeline(pipeline_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update prompt template
   */
  async UpdatePromptTemplate(template_id: string, data: UpdatePromptTemplateRequest): Promise<PromptTemplateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.UpdatePromptTemplate(template_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update schedule
   */
  async UpdateSchedule(schedule_id: string, data: UpdateScheduleRequest): Promise<PipelineScheduleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.UpdateSchedule(schedule_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update test case
   */
  async UpdateTestCase(test_case_id: string, data: UpdateTestCaseRequest): Promise<TestCaseResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.UpdateTestCase(test_case_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Validate pipeline YAML
   */
  async ValidatePipelineYAML(data: ValidatePipelineYAMLRequest): Promise<ValidatePipelineYAMLResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await workflowApi.ValidatePipelineYAML(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const workflowStore = new WorkflowStore();
